import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import api from '../config/api';
import { currentLanguage } from '../i18n';
import { errorMessage } from '../errors';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { Alert, Button, Spinner, TextField, Textarea } from '../ui';
import './auth.css';

const PATHS = { login: '/login', request: '/request-account', forgot: '/forgot' };

/** A text field of the auth card (the design system's, full width). */
function Field(props) {
  return <TextField {...props} />;
}

const TITLES = {
  login: 'auth.login.title',
  request: 'auth.request.title',
  forgot: 'auth.forgot.title',
  password: 'auth.password.title',
  invite: 'auth.invite.title',
};

// mode comes from the URL (see AuthGate): login, request, forgot, invite or
// password; token is set for the last two. The component stays mounted while
// the mode changes, so the e-mail typed on one screen is still there on the next.
function AuthScreens({ mode, token, onLoggedIn }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState({ email: '', password: '', display_name: '', message: '' });
  const [info, setInfo] = useState(null);       // invitation or token details
  const [notice, setNotice] = useState('');
  // The failed request itself, not its text, so the message follows a language switch.
  const [error, setError] = useState(null);
  // Kept as a flag, not a string, so the message follows a language switch.
  const [linkInvalid, setLinkInvalid] = useState(() => !!(location.state && location.state.invalidLink));
  const [done, setDone] = useState(false);      // a final message replaces the form
  const [busy, setBusy] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  // Keeps the "where to go after login" state while moving between auth screens.
  const go = (next, keepNotice = false) => {
    setError(null);
    setLinkInvalid(false);
    if (!keepNotice) setNotice('');
    setDone(false);
    const { invalidLink, ...state } = location.state || {}; // eslint-disable-line no-unused-vars
    navigate(PATHS[next], { state });
  };

  useEffect(() => {
    if (mode !== 'invite' && mode !== 'password') return;
    let cancelled = false;
    setInfo(null);
    setError(null);
    setNotice('');
    setDone(false);
    const url = mode === 'invite' ? `/auth/invitations/${token}` : `/auth/password-tokens/${token}`;
    api.get(url)
      .then((r) => { if (!cancelled) setInfo(r.data); })
      .catch((e) => {
        if (cancelled) return;
        setError(e);
        navigate('/login', { replace: true });
      });
    return () => { cancelled = true; };
  }, [mode, token, navigate]);

  const submit = (action) => async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  const login = submit(async () => {
    const r = await api.post('/auth/login', { email: form.email, password: form.password });
    onLoggedIn(r.data.user);
  });

  const request = submit(async () => {
    await api.post('/auth/request-account', { email: form.email, display_name: form.display_name, message: form.message, locale: currentLanguage() });
    setNotice(t('auth.request.sent'));
    setDone(true);
  });

  const forgot = submit(async () => {
    await api.post('/auth/forgot-password', { email: form.email });
    setNotice(t('auth.forgot.sent'));
    setDone(true);
  });

  const choosePassword = submit(async () => {
    const r = await api.post(`/auth/password-tokens/${token}`, { password: form.password });
    // Either way the token is spent, and both paths replace its URL so it
    // leaves the address bar and the history.
    if (r.data.user) {
      onLoggedIn(r.data.user); // navigates with replace
    } else {
      setForm({ ...form, password: '' });
      setNotice(t('auth.password.saved'));
      navigate('/login', { replace: true });
    }
  });

  const acceptInvite = submit(async () => {
    await api.post(`/auth/invitations/${token}/accept`, { display_name: form.display_name, password: form.password, locale: currentLanguage() });
    setForm({ ...form, password: '' });
    setNotice(t('auth.invite.created'));
    setDone(true);
    // The invitation is spent: take its URL out of the address bar and history.
    navigate('/login', { replace: true });
  });

  const backToLogin = (
    <p className="auth-links auth-links--center">
      <Button variant="ghost" size="sm" onClick={() => go('login')}>{t('auth.backToLogin')}</Button>
    </p>
  );

  return (
    <div className="auth-page">
      <main className="auth-card" aria-labelledby="auth-title">
        <div className="auth-card__top">
          <span className="auth-logo">
            <span className="auth-logo__mark" aria-hidden="true">I</span>
            <span className="auth-logo__word">Inventorium</span>
          </span>
          <LanguageSwitcher />
        </div>
        <h1 id="auth-title" className="auth-title">{t(TITLES[mode] || TITLES.login)}</h1>

        <div className="auth-messages">
          {notice && <Alert tone="success">{notice}</Alert>}
          {error && <Alert tone="danger">{errorMessage(t, error, 'auth.errors.generic')}</Alert>}
          {!error && linkInvalid && <Alert tone="danger">{t('auth.errors.invalidLink')}</Alert>}
        </div>

        {done && backToLogin}

        {!done && mode === 'login' && (
          <form onSubmit={login} className="auth-form">
            <Field label={t('auth.email')} type="email" autoComplete="username" required value={form.email} onChange={set('email')} />
            <Field label={t('auth.password.label')} type="password" autoComplete="current-password" required maxLength={72} value={form.password} onChange={set('password')} />
            <Button type="submit" block loading={busy}>{t('auth.login.submit')}</Button>
            <p className="auth-links">
              <Button variant="ghost" size="sm" onClick={() => go('forgot')}>{t('auth.login.forgot')}</Button>
              <Button variant="ghost" size="sm" onClick={() => go('request')}>{t('auth.login.request')}</Button>
            </p>
          </form>
        )}

        {!done && mode === 'request' && (
          <form onSubmit={request} className="auth-form">
            <Field label={t('auth.yourName')} autoComplete="name" required value={form.display_name} onChange={set('display_name')} />
            <Field label={t('auth.email')} type="email" autoComplete="email" required value={form.email} onChange={set('email')} />
            <Textarea label={t('auth.request.why')} rows={3} value={form.message} onChange={set('message')} />
            <Button type="submit" block loading={busy}>{t('auth.request.submit')}</Button>
            {backToLogin}
          </form>
        )}

        {!done && mode === 'forgot' && (
          <form onSubmit={forgot} className="auth-form">
            <p className="auth-intro">{t('auth.forgot.intro')}</p>
            <Field label={t('auth.email')} type="email" autoComplete="email" required value={form.email} onChange={set('email')} />
            <Button type="submit" block loading={busy}>{t('auth.forgot.submit')}</Button>
            {backToLogin}
          </form>
        )}

        {!done && (mode === 'password' || mode === 'invite') && !info && !error && (
          <p className="auth-checking"><Spinner size={18} /> {t('auth.checkingLink')}</p>
        )}

        {!done && mode === 'password' && info && (
          <form onSubmit={choosePassword} className="auth-form">
            <p className="auth-intro"><Trans i18nKey="auth.password.choose" values={{ email: info.email }} components={{ strong: <strong /> }} /></p>
            <Field label={t('auth.password.new')} type="password" autoComplete="new-password" minLength={10} maxLength={72} required value={form.password} onChange={set('password')} />
            <Button type="submit" block loading={busy}>{t('auth.password.submit')}</Button>
          </form>
        )}

        {!done && mode === 'invite' && info && (
          <form onSubmit={acceptInvite} className="auth-form">
            <p className="auth-intro"><Trans i18nKey="auth.invite.intro" values={{ inviter: info.inviter_name, email: info.email }} components={{ strong: <strong /> }} /></p>
            {info.message && <blockquote className="auth-quote">{info.message}</blockquote>}
            <Field label={t('auth.yourName')} autoComplete="name" required value={form.display_name} onChange={set('display_name')} />
            <Field label={t('auth.invite.password')} type="password" autoComplete="new-password" minLength={10} maxLength={72} required value={form.password} onChange={set('password')} />
            <Button type="submit" block loading={busy}>{t('auth.invite.submit')}</Button>
          </form>
        )}
      </main>
    </div>
  );
}

export default AuthScreens;
